/** @format */

import { FC } from "react";
import type { Step } from "../../types/sop";
import { cn } from "../../utils/cn";

interface StepSelectorListProps {
  steps: Step[];
  selectedIndices: number[];
  onSelectionChange: (indices: number[]) => void;
}

const StepSelectorList: FC<StepSelectorListProps> = ({
  steps,
  selectedIndices,
  onSelectionChange,
}) => {
  const toggleIndex = (idx: number) => {
    if (selectedIndices.includes(idx)) {
      onSelectionChange(selectedIndices.filter((i) => i !== idx));
    } else {
      onSelectionChange([...selectedIndices, idx]);
    }
  };

  const allSelected =
    steps.length > 0 && selectedIndices.length === steps.length;

  const handleToggleAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(steps.map((_, idx) => idx));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-200">
          Select steps to regenerate with AI
        </p>
        <button
          type="button"
          onClick={handleToggleAll}
          className="text-xs text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline"
        >
          {allSelected ? "Deselect all" : "Select all"}
        </button>
      </div>

      <div className="modal-body-scroll max-h-64 overflow-y-auto rounded-xl border border-slate-700/60 bg-slate-900/40 divide-y divide-slate-800/60 mr-2">
        {steps.map((step, idx) => {
          const checked = selectedIndices.includes(idx);
          return (
            <label
              key={idx}
              className={cn(
                "flex items-start gap-3 px-4 py-3 cursor-pointer text-sm",
                "hover:bg-slate-800/60 transition-colors",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-600 text-blue-500 bg-slate-950 focus:ring-blue-500/40"
                checked={checked}
                onChange={() => toggleIndex(idx)}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center text-[10px] font-semibold rounded-full px-2 py-0.5 bg-slate-800/80 text-slate-200 border border-slate-600/60">
                    Step {idx + 1}
                  </span>
                  <span className="truncate text-slate-100">
                    {step.title || "Untitled step"}
                  </span>
                </div>
                {step.details && (
                  <p className="mt-1 text-xs text-slate-400 line-clamp-2">
                    {step.details.replace(/<[^>]+>/g, " ").slice(0, 160)}
                  </p>
                )}
              </div>
            </label>
          );
        })}

        {steps.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500 text-center">
            No steps available yet. Generate or edit steps in the SOP editor
            first.
          </div>
        )}
      </div>
    </div>
  );
};

export default StepSelectorList;
